/**
 * The minimised upload, pinned to the corner of every page.
 *
 * This is what makes closing the upload dialog safe: the transfers keep their own
 * visible home, so walking to another creative never feels like abandoning them.
 * Hidden entirely when the queue is empty, and never shown on the Upload Center
 * itself, where the same thing is already on screen in full.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CloudUpload, Check, AlertTriangle } from 'lucide-react';
import { useUploadCenter, formatBytes, formatSpeed, formatEta } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

/** `to`: where a click goes. Staff open the Upload Center; the client portal has no such
 *  page, so it passes its own upload page instead. */
export default function UploadDock({ to = '/uploads' }: { to?: string }) {
  const { active, queued, failed, overallPct, pendingBytes, jobs, doneThisRun, totalThisRun,
          bytesPerSec, etaSeconds } = useUploadCenter();
  const { pathname } = useLocation();

  const live = active + queued;
  // A finished run shows "Done" for a few seconds, so whoever uploaded sees it land
  // (the clinic guide tells them to wait for it) before the dock closes.
  const [showDone, setShowDone] = useState(false);
  useEffect(() => {
    if (live > 0) { setShowDone(false); return; }
    if (failed === 0 && doneThisRun > 0) {
      setShowDone(true);
      const t = setTimeout(() => setShowDone(false), 8000);
      return () => clearTimeout(t);
    }
  }, [live, failed, doneThisRun]);
  const done = live === 0 && failed === 0 && showDone;
  // Once everything settles the dock stays only while something needs attention.
  if (live === 0 && failed === 0 && !done) return null;
  if (pathname === '/uploads') return null;
  if (jobs.length === 0) return null;

  return (
    <Link
      to={to}
      data-testid="upload-dock"
      className={cn(
        'fixed bottom-4 right-4 z-40 w-64 rounded-xl border bg-bg-card shadow-lg px-3 py-2.5 transition',
        failed > 0 && live === 0 ? 'border-red-500/40' : done ? 'border-emerald-500/50' : 'border-slate-800 hover:border-accent-400/50',
      )}
    >
      <div className="flex items-center gap-2">
        {live > 0
          ? <CloudUpload size={14} className="text-accent-400 shrink-0" />
          : failed > 0
            ? <AlertTriangle size={14} className="text-red-400 shrink-0" />
            : <Check size={14} className="text-emerald-400 shrink-0" />}
        <span className="text-xs text-muted-strong font-medium">
          {live > 0 ? `Uploading ${live} file${live === 1 ? '' : 's'}` : done ? `Done: ${doneThisRun} uploaded` : `${failed} failed`}
        </span>
        {live > 0 && overallPct !== null && (
          <span className="ml-auto text-[11px] text-accent-400">{overallPct}%</span>
        )}
      </div>

      {live > 0 && overallPct !== null && (
        <div className="h-1 rounded bg-slate-800 mt-2 overflow-hidden">
          <div className="h-full bg-accent-500 transition-all" style={{ width: `${overallPct}%` }} />
        </div>
      )}

      {live > 0 && (
        <div className="text-[11px] text-muted mt-1.5 space-y-0.5">
          <div>{doneThisRun} of {totalThisRun} - {formatBytes(pendingBytes)} left{failed ? ` - ${failed} failed` : ''}</div>
          {(bytesPerSec || etaSeconds !== null) && (
            <div className="text-muted-strong">
              {formatSpeed(bytesPerSec)}
              {bytesPerSec && etaSeconds !== null ? ' · ' : ''}
              {etaSeconds !== null ? `${formatEta(etaSeconds)} left` : ''}
            </div>
          )}
        </div>
      )}
    </Link>
  );
}
